import { invisiblyApi } from '../api/invisibly-api';
import type {
  AnomalyCheckRow,
  EtlDailyRollupResponse,
  EtlHealthQueryParams,
  EtlHealthRowsResponse,
  MartVerificationRow,
  PlatformReconciliationRow,
  EtlDataStatus,
  EtlPipelineStatus,
  PlatformHealthDetail,
  PlatformHealthResponse,
} from '../../pages/operations/types/operations.types';
import type {
  CreateScheduledTaskPayload,
  RegisteredSchedulerTask,
  ScheduledTask,
  SchedulerFailuresAlert,
  TaskRunLog,
  UpdateScheduledTaskPayload,
} from '../../pages/operations/types/scheduler.types';

const operationsEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (build) => ({
    getEtlPipelineStatus: build.query<EtlPipelineStatus, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/etl/pipeline-status',
      }),
      transformResponse: (response: { item: EtlPipelineStatus }) => response.item,
    }),
    getEtlDataStatus: build.query<EtlDataStatus, { date: string }>({
      query: ({ date }) => ({
        method: 'get',
        url: 'b2b/operations/etl/data-status',
        params: { date },
      }),
      transformResponse: (response: { item: EtlDataStatus }) => response.item,
    }),
    getPlatformHealth: build.query<PlatformHealthResponse, { date?: string }>({
      query: ({ date } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/platform-health',
        ...(date ? { params: { date } } : {}),
      }),
      transformResponse: (response: { item: PlatformHealthResponse }) => response.item,
    }),
    getPlatformHealthDetail: build.query<
    PlatformHealthDetail,
    { platform: string; date: string }
    >({
      query: ({ platform, date }) => ({
        method: 'get',
        url: 'b2b/operations/etl/platform-health/detail',
        params: { platform, date },
      }),
      transformResponse: (response: { item: PlatformHealthDetail }) => response.item,
    }),
    getEtlDailyRollup: build.query<EtlDailyRollupResponse, EtlHealthQueryParams | void>({
      query: ({ days = 30, refresh } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/daily-rollup',
        params: {
          days,
          ...(refresh ? { refresh } : {}),
        },
      }),
      transformResponse: (response: { item: EtlDailyRollupResponse }) => response.item,
    }),
    getEtlPlatformReconciliation: build.query<
    EtlHealthRowsResponse<PlatformReconciliationRow>,
    EtlHealthQueryParams | void
    >({
      query: ({ days = 30, refresh } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/platform-reconciliation',
        params: {
          days,
          ...(refresh ? { refresh } : {}),
        },
      }),
      transformResponse: (
        response: { item: EtlHealthRowsResponse<PlatformReconciliationRow> },
      ) => response.item,
    }),
    getEtlAnomalyChecks: build.query<
    EtlHealthRowsResponse<AnomalyCheckRow>,
    EtlHealthQueryParams | void
    >({
      query: ({ days = 30, refresh } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/anomaly-checks',
        params: {
          days,
          ...(refresh ? { refresh } : {}),
        },
      }),
      transformResponse: (response: { item: EtlHealthRowsResponse<AnomalyCheckRow> }) => response.item,
    }),
    getEtlMartVerification: build.query<
    EtlHealthRowsResponse<MartVerificationRow>,
    EtlHealthQueryParams | void
    >({
      query: ({ days = 30, refresh } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/mart-verification',
        params: {
          days,
          ...(refresh ? { refresh } : {}),
        },
      }),
      transformResponse: (
        response: { item: EtlHealthRowsResponse<MartVerificationRow> },
      ) => response.item,
    }),
    getScheduledTasks: build.query<ScheduledTask[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/schedules/tasks',
      }),
      transformResponse: (response: { item: ScheduledTask[] }) => response.item,
      providesTags: [{ type: 'SchedulerTasks', id: 'LIST' }],
    }),
    getScheduledTaskRegistry: build.query<RegisteredSchedulerTask[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/schedules/tasks/registry',
      }),
      transformResponse: (response: { item: RegisteredSchedulerTask[] }) => response.item,
    }),
    getScheduledTaskLogs: build.query<TaskRunLog[], { taskId: number; limit?: number }>({
      query: ({ taskId, limit = 50 }) => ({
        method: 'get',
        url: `b2b/operations/schedules/tasks/${taskId}/logs`,
        params: { limit },
      }),
      transformResponse: (response: { item: TaskRunLog[] }) => response.item,
    }),
    getActiveScheduledTasks: build.query<ScheduledTask[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/schedules/tasks/active',
      }),
      transformResponse: (response: { item: ScheduledTask[] }) => response.item,
    }),
    getSchedulerFailures: build.query<SchedulerFailuresAlert, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/schedules/alerts/failures',
      }),
      transformResponse: (response: { item: SchedulerFailuresAlert }) => response.item,
      providesTags: [{ type: 'SchedulerTasks', id: 'FAILURES' }],
    }),
    createScheduledTask: build.mutation<ScheduledTask, CreateScheduledTaskPayload>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/schedules/tasks',
        data: body,
      }),
      transformResponse: (response: { item: ScheduledTask }) => response.item,
      invalidatesTags: [
        { type: 'SchedulerTasks', id: 'LIST' },
        { type: 'SchedulerTasks', id: 'FAILURES' },
      ],
    }),
    updateScheduledTask: build.mutation<ScheduledTask, { id: number; body: UpdateScheduledTaskPayload }>({
      query: ({ id, body }) => ({
        method: 'patch',
        url: `b2b/operations/schedules/tasks/${id}`,
        data: body,
      }),
      transformResponse: (response: { item: ScheduledTask }) => response.item,
      invalidatesTags: [
        { type: 'SchedulerTasks', id: 'LIST' },
        { type: 'SchedulerTasks', id: 'FAILURES' },
      ],
    }),
  }),
});

export const {
  useGetEtlPipelineStatusQuery,
  useGetEtlDataStatusQuery,
  useGetPlatformHealthQuery,
  useGetPlatformHealthDetailQuery,
  useGetEtlDailyRollupQuery,
  useGetEtlPlatformReconciliationQuery,
  useGetEtlAnomalyChecksQuery,
  useGetEtlMartVerificationQuery,
  useGetScheduledTasksQuery,
  useGetScheduledTaskRegistryQuery,
  useGetScheduledTaskLogsQuery,
  useGetActiveScheduledTasksQuery,
  useGetSchedulerFailuresQuery,
  useCreateScheduledTaskMutation,
  useUpdateScheduledTaskMutation,
} = operationsEndpoints;

export default operationsEndpoints;
